import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PlusIcon } from '../../../assets/icons/element-plus-icon.svg';
import { Amenity } from '../../../interface/station-management.interface';
import formatToCamelCase from '../../../shared-functions/formatToCamelCase';

import SelectAmenitiesV2 from './SelectAmenitiesV2';

interface AmenitiesInputProperties {
  selectedAmenities: Amenity[];
  handleStationDetailChange: (name: string, changeValue: Amenity[]) => void;
}

/**
 * Amenities input with dropdown to select amenities
 */
function AmenitiesInput({
  selectedAmenities,
  handleStationDetailChange,
}: AmenitiesInputProperties) {
  const { t } = useTranslation('stationManagement');
  const [openDropdown, setOpenDropdown] = useState(false);

  const dropdownReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /**
     * Close dropdown when clicking outside
     */
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownReference.current && !dropdownReference.current.contains(event.target as Node)) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="amenities-input-wrap" ref={dropdownReference}>
      <p className="amenities-label">{t('amenities')}</p>

      <div
        className="amenities-input-box"
        onClick={() => setOpenDropdown((previous) => !previous)}
        role="presentation">
        {selectedAmenities.length === 0 ? (
          <span className="placeholder">{t('addAmenities')}</span>
        ) : (
          <div className="selected-amenities">
            {selectedAmenities.map((amenity) => (
              <span className="amenity-tag" key={amenity}>
                {t(formatToCamelCase(amenity))}
              </span>
            ))}
          </div>
        )}

        <div className="dropdown-icon">
          <PlusIcon />
        </div>
      </div>

      {openDropdown && (
        <div className="amenities-dropdown">
          <SelectAmenitiesV2
            selectedAmenities={selectedAmenities}
            handleStationDetailChange={handleStationDetailChange}
          />
        </div>
      )}
    </div>
  );
}

export default AmenitiesInput;
